import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '../components/ui/input';
import { MapPin, Loader2, X } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/utils';

/**
 * AddressAutocomplete component with OpenStreetMap/Nominatim integration
 * Features:
 * - Debounce (300ms default)
 * - Min 3 chars required
 * - Keyboard navigation
 * - Click outside to close
 * - Mobile-friendly
 */
export function AddressAutocomplete({ 
  value = '',
  onChange,
  onSelect,
  placeholder = 'Enter address...',
  className,
  disabled = false,
  debounceMs = 300,
  testId = 'address-autocomplete'
}) {
  const { api } = useAuth();
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [error, setError] = useState(null);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimerRef = useRef(null);
  
  // Sync external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);
  
  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && 
        !inputRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Fetch suggestions with debounce
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/address/search?q=${encodeURIComponent(query)}&limit=5`);
      setSuggestions(response.data || []);
      setShowDropdown(true);
      setHighlightedIndex(-1);
    } catch (err) {
      console.error('Address search error:', err);
      setError('Failed to fetch address suggestions');
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [api]);
  
  // Debounced search
  const debouncedSearch = useCallback((query) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(query);
    }, debounceMs);
  }, [fetchSuggestions, debounceMs]);
  
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);
  
  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    if (onChange) {
      onChange(newValue);
    }
    
    debouncedSearch(newValue);
  };
  
  // Handle suggestion selection
  const handleSelect = (suggestion) => {
    // P1 FIX: preserve user-entered street number when provider downgrades to road-level result.
    // Example: user types "109 Mount Vincent Road East Maitland" but Nominatim returns
    // "Mount Vincent Road, East Maitland..." without house number.
    const typedValue = (inputValue || '').trim();
    const typedStreetNumber = typedValue.match(/^\s*(\d+[A-Za-z]?)\b/)?.[1] || '';
    const providerStreetNumber = suggestion.street_number || '';

    const providerAddress = suggestion.display_name || [
      suggestion.street_number,
      suggestion.street_name,
      suggestion.suburb || suggestion.city,
      suggestion.state,
      suggestion.postcode
    ].filter(Boolean).join(' ');

    let formattedAddress = providerAddress;

    if (typedStreetNumber && !providerStreetNumber) {
      const displayLower = providerAddress.toLowerCase();
      const typedLower = typedValue.toLowerCase();

      // If the selected provider result still matches the typed street locality,
      // keep the user's full typed address as the saved address text.
      const streetName = (suggestion.street_name || '').toLowerCase();
      const suburb = (suggestion.suburb || suggestion.city || '').toLowerCase();

      const looksLikeSameRoad =
        (streetName && typedLower.includes(streetName)) ||
        (suburb && typedLower.includes(suburb)) ||
        typedLower.split(/\\s+/).slice(1, 4).every(part => displayLower.includes(part));

      if (looksLikeSameRoad) {
        formattedAddress = typedValue;
      }
    }

    setInputValue(formattedAddress);
    setSuggestions([]);
    setShowDropdown(false);
    
    if (onChange) {
      onChange(formattedAddress);
    }
    
    if (onSelect) {
      onSelect({
        address: formattedAddress,
        full_address: formattedAddress,
        formatted_address: formattedAddress,
        street_number: providerStreetNumber || typedStreetNumber || null,
        street_name: suggestion.street_name,
        suburb: suggestion.suburb,
        city: suggestion.city,
        state: suggestion.state,
        postcode: suggestion.postcode,
        lat: suggestion.lat,
        lng: suggestion.lng,
        place_id: suggestion.place_id,
      });
    }
  };
  
  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };
  
  // Clear input
  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setShowDropdown(false);
    if (onChange) onChange('');
    inputRef.current?.focus();
  };
  
  // Format suggestion display
  const formatSuggestion = (suggestion) => {
    const parts = [];
    
    if (suggestion.street_number && suggestion.street_name) {
      parts.push(`${suggestion.street_number} ${suggestion.street_name}`);
    } else if (suggestion.street_name) {
      parts.push(suggestion.street_name);
    }
    
    if (suggestion.suburb) parts.push(suggestion.suburb);
    if (suggestion.state) parts.push(suggestion.state);
    if (suggestion.postcode) parts.push(suggestion.postcode);
    
    return parts.length > 0 ? parts.join(', ') : suggestion.display_name;
  };
  
  return (
    <div className={cn('relative', className)}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue.length >= 3 && suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-10 pr-8 bg-[#09090b] border-[#3f3f46] text-white"
          data-testid={testId}
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-400 animate-spin" />
        )}
        {!isLoading && inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
            data-testid={`${testId}-clear`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      
      {/* Suggestions dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-[#18181b] border border-[#3f3f46] rounded-md shadow-lg max-h-60 overflow-y-auto"
          data-testid={`${testId}-dropdown`}
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.place_id || index}
              type="button"
              onClick={() => handleSelect(suggestion)}
              className={cn(
                'w-full px-4 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-700/50 transition-colors',
                'flex items-start gap-3',
                index === highlightedIndex && 'bg-zinc-700/50'
              )}
              data-testid={`${testId}-suggestion-${index}`}
            >
              <MapPin className="w-4 h-4 mt-0.5 text-zinc-500 flex-shrink-0" />
              <div>
                <div className="font-medium">
                  {(() => {
                    const typedStreetNumber =
                      (inputValue || '').match(/^\s*(\d+[A-Za-z]?)\b/)?.[1] || '';

                    const providerStreetNumber =
                      suggestion.street_number || '';

                    let displayAddress = formatSuggestion(suggestion);

                    if (
                      typedStreetNumber &&
                      !providerStreetNumber &&
                      displayAddress &&
                      !displayAddress.startsWith(typedStreetNumber)
                    ) {
                      displayAddress = `${typedStreetNumber} ${displayAddress}`;
                    }

                    return displayAddress;
                  })()}
                </div>
                {suggestion.display_name.length > 50 && (
                  <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">
                    {(() => {
                      const typedStreetNumber =
                        (inputValue || '').match(/^\s*(\d+[A-Za-z]?)\b/)?.[1] || '';

                      const providerStreetNumber =
                        suggestion.street_number || '';

                      let displayName = suggestion.display_name;

                      if (
                        typedStreetNumber &&
                        !providerStreetNumber &&
                        displayName &&
                        !displayName.startsWith(typedStreetNumber)
                      ) {
                        displayName = `${typedStreetNumber} ${displayName}`;
                      }

                      return displayName;
                    })()}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <p className="text-xs text-red-400 mt-1">{error}</p>
      )}
      
      {/* Min chars hint */}
      {inputValue.length > 0 && inputValue.length < 3 && !showDropdown && (
        <p className="text-xs text-zinc-500 mt-1">Type at least 3 characters to search</p>
      )}
    </div>
  );
}

export default AddressAutocomplete;
